
import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, ITournamentEditionMatch, ITournamentEditionWebsite, OmneHostService, SanitizedResponse } from "../../../../services";
import { Classname, Paginator, Scheme, useSearchParams } from "@rivtn/noseur";
import { Utils } from "../../../../utils";

const dotClass = (m: ITournamentEditionMatch) =>
    m.status === "ONGOING"
        ? styles["sdot-ongoing"]
        : m.status === "UPCOMING"
            ? styles["sdot-upcoming"]
            : styles["sdot-d"];

const scoreColor = (m: ITournamentEditionMatch) =>
    m.status === "ONGOING"
        ? "var(--green)"
        : "var(--muted)";

export function buildMatchView(publicId: string, t: ITournamentEditionMatch) {
    const ownScores = Object.values(Object.values(t.scores)?.[0] ?? {}).map((s) => Object.values(s)?.[0]);
    const won = (ownScores?.[0] ?? 0) > ownScores?.[1];
    const [first, ...others] = t.tournament_edition_registered_participant_ids?.map((external_id) => ({ ...t.extra_data?.participants_profiles?.[external_id], external_id }));

    return (<a key={t.external_id} className={Classname.build(styles["match-row"], styles["unlinked"], t.status === "ONGOING" ? styles["m-ongoing"] : "")} target="_blank" href={Utils.buildTournamentMatchLink(publicId, t)}>
        <div className={styles["match-team"]}>
            <img className={styles["match-flag"]} alt={first.external_id} src={first.profile_picture_url ?? first.logo_url} />
            <span>{first.name ?? first.username}</span>
        </div>

        <div className={styles["match-center"]}>
            {(!!ownScores.length)
                ? (<div className={styles["match-score"]} style={{ color: scoreColor(t) }}>
                    {ownScores?.[0] ?? ""} - {ownScores?.slice(1)?.join(", ") ?? ""}
                </div>)
                : (<div className={styles["match-vs"]}>VS</div>)}

            <div className={styles["match-round"]}>
                <span className={`${styles["sdot"]} ${dotClass(t)}`} />
                {Utils.fineDate(new Date(t.start_date))}
            </div>
        </div>

        <div className={`${styles["match-team"]} ${styles["right"]}`}>
            {others.reduce((acc: any[], other: any, index: number) => {
                acc.push(<React.Fragment key={"" + index + other.external_id}>
                    <img className={styles["match-flag"]} alt={other.external_id} src={first.profile_picture_url ?? first.logo_url} />
                    <span className="name">{other.name ?? other.username}</span>
                </React.Fragment>);
                if (index < (others.length - 1)) acc.push(<span key={"sep" + index + other.external_id} style={{ marginLeft: -10 }}>,</span>);
                return acc;
            }, [])}
        </div>
    </a>);
}

export interface ScheduleProps {
    basePath: string;
    publicId: string;
    website: ITournamentEditionWebsite;
}

function Schedule(props: ScheduleProps) {

    const { publicId, website } = props;
    const omnehostService = OmneHostService.getInstance();
    const sections = [["UPCOMING", "Upcoming"], ["ONGOING", "🔴 Live"], ["ENDED", "Past"]];
    const [matches, setMatches] = React.useState<ApiResponseData<ITournamentEditionMatch>>();
    const { page, setPage, status, setStatus, } = useSearchParams({ defaultInit: window.location.search, params: { page: 1, status: "", } });

    React.useEffect(fetchInformation, [omnehostService, page, status]);

    return (
        <div className={styles["section"]}>
            <div className={styles["sec-line"]} />
            <h1 className={styles["sec-title"]}>Match <em>Schedule</em></h1>
            <p className={styles["sec-sub"]}>All fixtures — upcoming, live and completed</p>

            <div className={styles["tabs"]}>
                {[["", "All Matches"], ...sections].map(([v, l]) => (
                    <button key={v} className={`${styles["tab-btn"]}${status === v ? ` ${styles["active"]}` : ""}`} onClick={() => setStatus(v)}>{l}</button>
                ))}
            </div>

            {(!!matches?.content)
                ? ((matches.content && matches.content?.length > 0)
                    ? (<div className={styles["matches-list"]}>
                        {matches.content!.map((t) => buildMatchView(publicId, t))}
                    </div>)
                    : (Utils.emptyState()))
                : (Utils.loadingState())}

            <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={matches?.size} totalRecords={matches?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
                setPage(e.currentPage)
            }} template={{
                activePageLabel: () => "",
                nextPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                previousPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                lastPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                firstPageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                pageElement: (option) => (<button className={Classname.build(`${styles["pg-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
            }} />

        </div>
    );

    function fetchInformation() {
        setMatches({});
        omnehostService.queryTournamentEditionSchedule(website.tournament_edition.external_id, {
            page,
            size: 10,
            status__eq: status,
            sort: ["start_date,ASC", "status,ASC"],
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEditionMatch>) => {
            apiResponse.data!.content?.forEach((c) => c.tournament_edition = website.tournament_edition);
            setMatches(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default Schedule;
